import React, { createElement, Fragment } from "react";

import {
    AutocompleteState,
    createAutocomplete
} from "@algolia/autocomplete-core";
import {
    getAlgoliaResults,
    parseAlgoliaHitHighlight
} from "@algolia/autocomplete-preset-algolia";
import { Hit } from "@algolia/client-search";
import { css } from "@compiled/react";
/** https://github.com/algolia/algoliasearch-client-javascript/issues/1381 */
import algoliasearch from "algoliasearch/dist/algoliasearch-lite.esm.browser";

import SearchIcon from "@atlaskit/icon/glyph/search";

import { Cross } from "@/libraries/wpl/foundations/src/";
import "@algolia/autocomplete-theme-classic";

const ALGOLIA_APP_ID = process.env["ALGOLIA_APP_ID"] as string;
const ALGOLIA_SEARCH_API_KEY = process.env["ALGOLIA_SEARCH_API_KEY"] as string;
const ALGOLIA_SEARCH_INDEX_NAME = "www.atlassian.com.v1";
const ALGOLIA_FACET_KEY = "category";

const algoliaSearchClient = algoliasearch(
    ALGOLIA_APP_ID,
    ALGOLIA_SEARCH_API_KEY
);

type ContentHit = Hit<{
    brand: string;
    categories: string[];
    description: string;
    image: string;
    name: string;
    title: string;
    price: number;
    rating: number;
    type: string;
    url: string;
}>;

type ItemUrlProps = {
    item: ContentHit;
};

type HighlightHitParams = {
    hit: ContentHit;
    attribute:
        | "name"
        | "image"
        | "url"
        | "brand"
        | string[]
        | "categories"
        | "description"
        | "price"
        | "rating"
        | "type"
        | "objectID"
        | "_highlightResult"
        | "_snippetResult"
        | "_rankingInfo"
        | "_distinctSeqID";
    tagName?: string;
};

const SearchBarStyles = css({
    "position": "relative",
    "bottom": 0,
    "margin": "20px",
    "width": "auto",
    "maxWidth": "100%",
    "zIndex": 5,
    "display": "block",
    "justifyContent": "center",

    ".aa-Form, .aa-Form:focus-within": {
        border: "none",
        borderColor: "transparent",
        outline: "solid transparent",
        boxShadow: "none"
    },

    ".aa-Item": {
        padding: "16px, 8px"
    },

    ".aa-Panel": {
        borderRadius: "15px 15px 15px 15px"
    },

    ".aa-InputWrapper": {
        position: "relative",
        display: "flex",
        verticalAlign: "middle",
        alignItems: "center",
        height: "2.5rem",
        width: "895px",
        borderWidth: "1px",
        borderStyle: "solid",
        borderRadius: "25px 25px 25px 25px",
        padding: "0 16px",
        borderColor: "#F4F5F7",
        backgroundColor: "#F4F5F7",
        margin: "16px"
    },

    ".close-button": {
        color: "#44546F",
        backgroundColor: "transparent",
        insetInlineEnd: "10px",
        border: "none",
        insetBlockStart: 0,
        insetBlockEnd: 0,
        margin: "auto"
    }
});

const Highlight = ({
    hit,
    attribute,
    tagName = "mark"
}: HighlightHitParams) => {
    return createElement(
        Fragment,
        {},
        parseAlgoliaHitHighlight({ hit, attribute }).map(
            ({ value, isHighlighted }, index) => {
                if (isHighlighted) {
                    return createElement(tagName, { key: index }, value);
                }

                return value;
            }
        )
    );
};

const AutoCompleteSearchBar = ({
    setIsOpenSearch
}: {
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const [autocompleteState, setAutocompleteState] = React.useState<
        AutocompleteState<ContentHit>
    >({
        collections: [],
        completion: null,
        context: {},
        isOpen: false,
        query: "",
        activeItemId: null,
        status: "idle"
    });

    const autocomplete = React.useMemo(
        () =>
            createAutocomplete<
                ContentHit,
                React.BaseSyntheticEvent,
                React.MouseEvent,
                React.KeyboardEvent
            >({
                // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                onStateChange({ state }: { state: AutocompleteState<any> }) {
                    setAutocompleteState(state);
                },
                insights: true,
                getSources() {
                    return [
                        {
                            sourceId: ALGOLIA_FACET_KEY,
                            getItems({ query }: { query: string }) {
                                return getAlgoliaResults({
                                    searchClient: algoliaSearchClient,
                                    queries: [
                                        {
                                            indexName:
                                                ALGOLIA_SEARCH_INDEX_NAME,
                                            query,
                                            params: {
                                                hitsPerPage: 8
                                            }
                                        }
                                    ]
                                });
                            },
                            getItemUrl({ item }: ItemUrlProps) {
                                return item.url;
                            }
                        }
                    ];
                },
                placeholder: "",
                openOnFocus: false,
                stallThreshold: 500
            }),
        []
    );
    const inputRef = React.useRef<HTMLInputElement>(null);
    const formRef = React.useRef<HTMLFormElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const { getEnvironmentProps } = autocomplete;

    React.useEffect(() => {
        if (!formRef.current || !panelRef.current || !inputRef.current) {
            return undefined;
        }

        const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
            formElement: formRef.current,
            inputElement: inputRef.current,
            panelElement: panelRef.current
        });

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("touchstart", onTouchStart);
        window.addEventListener("touchmove", onTouchMove);

        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
        };
    }, [getEnvironmentProps, autocompleteState.isOpen]);

    return (
        <div
            className="aa-Autocomplete"
            css={SearchBarStyles}
            {...autocomplete.getRootProps({})}
        >
            <form
                ref={formRef}
                className="aa-Form"
                {...autocomplete.getFormProps({
                    inputElement: inputRef.current
                })}
            >
                <div className="aa-InputWrapperPrefix" />
                <div className="aa-InputWrapper">
                    <input
                        className="aa-Input"
                        ref={inputRef}
                        {...autocomplete.getInputProps({
                            inputElement: inputRef.current
                        })}
                    />
                    <label
                        className="aa-Label"
                        {...autocomplete.getLabelProps({})}
                    >
                        <button
                            className="aa-SubmitButton"
                            type="submit"
                            title="Submit"
                        >
                            <SearchIcon label={"search label"} />
                        </button>
                    </label>
                </div>
                <div className="aa-InputWrapperSuffix">
                    <button
                        className="aa-ClearButton"
                        title="Clear"
                        type="reset"
                        onClick={(e) => {
                            e.preventDefault();
                            setIsOpenSearch(false);
                        }}
                    >
                        <Cross size="24" />
                    </button>
                </div>
            </form>

            {autocompleteState.isOpen && (
                <div
                    ref={panelRef}
                    className={[
                        "aa-Panel",
                        "aa-Panel--desktop",
                        autocompleteState.status === "stalled" &&
                            "aa-Panel--stalled"
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    {...autocomplete.getPanelProps({})}
                >
                    <div className="aa-PanelLayout aa-Panel--scrollable">
                        {autocompleteState.collections.map(
                            (collection, index) => {
                                const { source, items } = collection;

                                return (
                                    <section
                                        key={`source-${index}`}
                                        className="aa-Source"
                                    >
                                        {items.length > 0 && (
                                            <ul
                                                className="aa-List"
                                                {...autocomplete.getListProps()}
                                            >
                                                {items.map((item) => {
                                                    return (
                                                        <li
                                                            key={item.objectID}
                                                            className="aa-Item"
                                                            {...autocomplete.getItemProps(
                                                                { item, source }
                                                            )}
                                                        >
                                                            <a
                                                                className="aa-ItemLink"
                                                                href={item.url}
                                                            >
                                                                <div className="aa-ItemWrapper">
                                                                    <div className="aa-ItemContent">
                                                                        <div className="aa-ItemContentBody">
                                                                            <div className="aa-ItemContentTitle">
                                                                                <strong>
                                                                                    {
                                                                                        item.title
                                                                                    }
                                                                                </strong>
                                                                            </div>
                                                                            <div className="aa-ItemContentDescription">
                                                                                <Highlight
                                                                                    hit={
                                                                                        item
                                                                                    }
                                                                                    attribute="description"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="aa-ItemActions">
                                                                        <button
                                                                            className="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly"
                                                                            type="button"
                                                                            title="Select"
                                                                            style={{
                                                                                pointerEvents:
                                                                                    "none"
                                                                            }}
                                                                        >
                                                                            <svg
                                                                                fill="currentColor"
                                                                                viewBox="0 0 24 24"
                                                                            >
                                                                                <path d="M18.984 6.984h2.016v6h-15.188l3.609 3.609-1.406 1.406-6-6 6-6 1.406 1.406-3.609 3.609h13.172v-4.031z" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </section>
                                );
                            }
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoCompleteSearchBar;
